import React, { useEffect, useState } from "react";
import axios from "axios";
import { Zap, Trophy, Flame, Repeat, Radar, Award, Medal, Crown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#D4AF37", RED = "#C41E24";

// Builds the scrolling items from /me/ticker + leaderboard "you" stats.
export const buildTickerItems = (t, you) => {
  const items = [];
  if (t?.personal_best) items.push({ icon: Zap, k: "Personal Best", v: `${t.personal_best.score.toFixed(1)} / 10`, s: t.personal_best.drill, c: GOLD });
  items.push({ icon: Repeat, k: "Total Reps", v: (t?.total_reps ?? 0).toLocaleString(), s: `${t?.sessions ?? 0} sessions logged`, c: GOLD });
  items.push({ icon: Flame, k: "Training Streak", v: `${t?.streak ?? 0} day${t?.streak === 1 ? "" : "s"}`, s: `longest ${t?.longest_streak ?? 0}`, c: RED });
  items.push({ icon: Radar, k: "Engine Scans", v: String(t?.total_scans ?? 0), s: `${t?.chapters ?? 0} chapters mastered`, c: GOLD });
  if (you) items.push({ icon: Trophy, k: "Leaderboard", v: `#${you.rank}`, s: `${(you.points || 0).toLocaleString()} pts`, c: RED });
  (t?.achievements || []).forEach((a) => items.push({
    icon: a.kind === "elite" ? Crown : a.kind === "badge" ? Medal : Award,
    k: "Achievement", v: a.label, s: a.date || "", c: a.kind === "elite" ? GOLD : RED,
  }));
  return items;
};

export const TickerStrip = ({ items, className = "", speed }) => {
  const loop = [...items, ...items];
  return (
    <div className={`overflow-hidden min-w-0 ${className}`}>
      <div className="ticker-track items-center" style={speed ? { animationDuration: speed } : undefined}>
        {loop.map((it, i) => (
          <div key={i} className="flex items-center gap-2.5 px-5 whitespace-nowrap" data-testid={i < items.length ? `ticker-item-${it.k.toLowerCase().replace(/\s+/g, "-")}` : undefined}>
            <it.icon size={12} style={{ color: it.c, filter: `drop-shadow(0 0 5px ${it.c})` }} />
            <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-white/40">{it.k}</span>
            <span className="font-mono text-xs font-bold" style={{ color: it.c, textShadow: `0 0 8px ${it.c}88` }}>{it.v}</span>
            {it.s && <span className="font-mono text-[9px] tracking-wider uppercase text-white/35">{it.s}</span>}
            <span className="ml-3 w-1 h-1 rounded-full bg-white/20" />
          </div>
        ))}
      </div>
    </div>
  );
};

// Fixed bottom strip on the Elite Hub — personal bests, reps, streaks, achievements.
export default function TechnicalTicker({ token, you }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/me/ticker`, { headers: { Authorization: `Bearer ${token}` } }).then(({ data }) => setData(data)).catch(() => {});
  }, [token]);
  if (!data) return null;
  const items = buildTickerItems(data, you);
  return (
    <div data-testid="technical-ticker" className="fixed inset-x-0 z-30 bottom-[4.4rem] md:bottom-0 border-t border-[#D4AF37]/30 bg-[#070707]/92 backdrop-blur-xl"
      style={{ boxShadow: "0 -6px 30px rgba(0,0,0,0.6), 0 -1px 18px rgba(212,175,55,0.18)" }}>
      <div className="flex items-stretch h-9">
        <div className="flex items-center gap-1.5 px-3 shrink-0 border-r border-[#D4AF37]/30 bg-[#D4AF37]/10">
          <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse" style={{ boxShadow: "0 0 8px #D4AF37" }} />
          <span className="font-mono text-[9px] font-bold tracking-[0.3em] uppercase text-[#D4AF37]">Live</span>
        </div>
        <TickerStrip items={items} className="flex-1" />
      </div>
    </div>
  );
}
